// src/components/lead/sections/ProductCard.tsx
import { useEffect, useState } from "react";
import type { ExtractedLeadV2, Lead } from "../../../types/lead";
import type { UpdateLeadFieldFn } from "../../../types/leadActions";
import { ProductCardOpportunityAction } from "./ProductCardOpportunityAction";

type Props = {
  lead: Lead;
  e: ExtractedLeadV2;
  showProductDetails: boolean;
  setShowProductDetails: (updater: (prev: boolean) => boolean) => void;
  updateCurrentLeadField: UpdateLeadFieldFn;
  hasTranscript: boolean;
  onLinkedOpportunity: (opportunityId: string) => void;
  onViewOpportunity: (opportunityId: string) => void;
  setToast: (t: { message: string; type: "success" | "error" } | null) => void;
};

function sizesToText(arr: ExtractedLeadV2["size_options"] | undefined | null): string {
  return Array.isArray(arr) ? arr.join(", ") : "";
}

function textToSizes(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function ProductCard(props: Props) {

  const {
    lead,
    e,
    showProductDetails,
    setShowProductDetails,
    updateCurrentLeadField,
    hasTranscript,
    onLinkedOpportunity,
    onViewOpportunity,
    setToast,
  } = props;

  // Local draft for the "All Sizes Mentioned" input so the user can type
  // spaces and trailing commas without them being trimmed on every keystroke.
  // We persist the *parsed* list to `e.size_options` on each change, but the
  // displayed value stays raw. Resyncs from external updates (transcript
  // fill, server load) when the parsed forms diverge. See dev-sync note
  // 8997097b-1f0a-41fb-8659-a41625082407.
  const [sizesDraft, setSizesDraft] = useState<string>(() => sizesToText(e.size_options));
  useEffect(() => {
    const external = sizesToText(e.size_options);
    const parsed = textToSizes(sizesDraft).join(", ");
    if (external !== parsed) setSizesDraft(external);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e.size_options]);

  return (
    <div data-app-panel="ProductCard" className="bg-gray-50 rounded-2xl p-4 border border-gray-200 dark:bg-zinc-900/40 dark:border-zinc-700">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-2 min-w-0">
        <div className="min-w-0 flex-1">
          <h3 className="text-xl font-semibold">Product</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {e.product_line || "No product line"} •{" "}
            {e.size_config ||
              (Array.isArray(e.size_options) && e.size_options.length > 0
                ? e.size_options.join(", ")
                : "No size yet")}{" "}
            •{" "}
            {e.enunciated_budget
              ? `Budget: ${e.enunciated_budget}`
              : "Budget not set"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <ProductCardOpportunityAction
            lead={lead}
            productLine={e.product_line}
            onLinkedOpportunity={onLinkedOpportunity}
            onViewOpportunity={onViewOpportunity}
            setToast={setToast}
          />
          <button
            type="button"
            onClick={() => setShowProductDetails((prev) => !prev)}
            className="shrink-0 text-xs text-blue-600 underline"
          >
            {showProductDetails ? "Hide details" : "Show details"}
          </button>
        </div>
      </div>

      {showProductDetails && (
        <>
          <div className="grid grid-cols-1 gap-3 mt-3"> {/* Product line */}
            <div> {/* Product line selector */}
              <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                Product Line
              </div>
              <select
                data-lead-field="product_line"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={e.product_line ?? ""}
                onChange={(ev) =>
                  updateCurrentLeadField(
                    "product_line",
                    ev.target.value as ExtractedLeadV2["product_line"]
                  )
                }
              >
                <option value="" disabled>— select product line —</option>
                <option value="Sheds">Sheds</option>
                <option value="Steel Structures">Steel Structures</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div> {/* Size / configuration */}
              <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                Size / Configuration
              </div>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder='e.g., "10x16 lofted barn with double doors"'
                value={e.size_config || ""}
                onChange={(ev) => updateCurrentLeadField("size_config", ev.target.value)}
              />
            </div>

            <div> {/* All sizes mentioned */}
              <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                All Sizes Mentioned
              </div>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                placeholder="e.g., 10x16, 12x24"
                value={sizesDraft}
                onChange={(ev) => {
                  const raw = ev.target.value;
                  setSizesDraft(raw);
                  const list = textToSizes(raw);
                  updateCurrentLeadField("size_options", list.length > 0 ? list : null);
                }}
              />
            </div>

            <div> {/* Budget */}
              <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                Budget (spoken)
              </div>
              <input
                data-lead-field="enunciated_budget"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="e.g., under $5,000 or $200/month"
                value={e.enunciated_budget || ""}
                onChange={(ev) => updateCurrentLeadField("enunciated_budget", ev.target.value)}
              />
            </div>

            <div> {/* Product interest summary */}
              <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                Product Interest Summary
              </div>
              {hasTranscript ? (
                <textarea
                  className="w-full border rounded-lg px-3 py-2 text-sm resize-y"
                  rows={3}
                  placeholder='e.g., "Sheds | Lofted Barn | 10x16 | Color: Barn Red | Options: Ramp, Radiant Barrier"'
                  value={e.product_interest_category || ""}
                  onChange={(ev) =>
                    updateCurrentLeadField("product_interest_category", ev.target.value)
                  }
                />
              ) : (
                <ProductInterestBlockedCallout existingValue={e.product_interest_category} />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Replacement for the Product Interest Summary textarea when no transcript
 * has been captured yet. Per dev-sync 4569f693 Part C2: this field is
 * transcript-derived; freeform info belongs in Notes. Offers a single
 * affordance — "Open notes" — that focuses the Notes textarea instead.
 *
 * Edge case: if `product_interest_category` was non-empty *before* the
 * transcript got cleared (rare but possible — e.g. user manually typed
 * pre-fix), surface the existing value as read-only so it's not lost from
 * view; the user can still copy it across to Notes by hand.
 */
function ProductInterestBlockedCallout({ existingValue }: { existingValue: string | null }) {
  const focusNotes = () => {
    if (typeof document === "undefined") return;
    const target = document.getElementById("lead-notes-textarea");
    if (target instanceof HTMLTextAreaElement) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      // Defer focus until after scroll-into-view kicks off so the caret
      // lands without competing with the smooth scroll animation.
      window.setTimeout(() => target.focus(), 250);
    }
  };

  return (
    <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50/60 px-3 py-3 text-xs dark:border-amber-700 dark:bg-amber-950/30">
      <p className="text-amber-900 dark:text-amber-100">
        <span className="font-semibold">Waiting on transcript.</span> This field is filled by transcript extraction.
        For freeform info about this lead, use Notes.
      </p>
      <button
        type="button"
        onClick={focusNotes}
        className="mt-2 inline-flex items-center rounded-md border border-amber-400 bg-white px-2.5 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100 dark:border-amber-600 dark:bg-zinc-900/40 dark:text-amber-100 dark:hover:bg-amber-950/50"
      >
        Open notes →
      </button>
      {existingValue && existingValue.trim() ? (
        <p className="mt-2 italic text-amber-900/80 dark:text-amber-100/80">
          Current value (read-only until transcript): &ldquo;{existingValue}&rdquo;
        </p>
      ) : null}
    </div>
  );
}
